<template>
  <div>
    <p style="margin-right: 20px;">{{ displayedComment }}</p>
    <div
      v-if="shouldShowLanguageLine"
      class="d-flex align-items-center"
      style="font-size: 13px; color: #1d2329;"
    >
      <template v-if="!isTranslationExpanded && !map.raw && isOriginalLangDifferent">
        <svg class="icon text-primary-blue me-2" width="16" height="16" fill="var(--primary-blue)">
          <use xlink:href="#___icon___translation"></use>
        </svg>
        <span class="me-1 text-grey-600">
          {{ content.reviews_originalLanguage || 'Langue d’origine' }} :
        </span>
        <span class="fw-bold me-1">{{ getLanguageLabel(review.language) }}</span>
      </template>

      <span
        v-if="isOriginalLangDifferent"
        @click="toggleTranslation"
        class="text-decoration-underline"
        style="color: black; cursor: pointer;"
      >
        {{ isTranslationExpanded
          ? content.reviews_seeTranslation || 'Voir la traduction'
          : content.reviews_see_original || 'Voir l’original' }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { ref, computed, watchEffect } from 'vue'

const props = defineProps({
  review: {
    type: Object,
    required: true
  }
})

const store = useStore()
const language = computed(() => store.state.language)
const content = computed(() => store.state.content)

function parseCustomerComment(rawComment) {
  if (!rawComment || typeof rawComment !== 'string') return {}
  if (!rawComment.match(/\b\w{2}:/)) return { raw: rawComment }

  const parts = rawComment.split(/\s*\|\s*/)
  const result = {}
  for (const part of parts) {
    const match = part.match(/^(\w{2}):\s*(.+)$/s)
    if (match) {
      const lang = match[1].toLowerCase().slice(0, 2)
      result[lang] = match[2].trim()
    }
  }
  return result
}

const normalizeLang = (lang) => lang?.toLowerCase().slice(0, 2) || ''
const siteLang = computed(() => normalizeLang(language.value))
const originalLang = computed(() => normalizeLang(props.review.language))
const isOriginalLangDifferent = computed(() => siteLang.value !== originalLang.value)

const currentLang = ref(siteLang.value)

function toggleTranslation() {
  currentLang.value = currentLang.value === originalLang.value ? siteLang.value : originalLang.value
}

const parsedComment = computed(() => parseCustomerComment(props.review.customerComment))
const map = parsedComment // alias for template

const displayedComment = computed(() => {
  const m = map.value
  const lang = currentLang.value
  if (m.raw) return m.raw
  return m[lang] || m[originalLang.value] || Object.values(m)[0] || ''
})

const isTranslationExpanded = computed(() => currentLang.value === originalLang.value)

const shouldShowLanguageLine = computed(() => {
  const m = map.value
  if (m.raw) {
    return siteLang.value !== originalLang.value
  }
  return Object.keys(m).length > 1
})

function getLanguageLabel(langCode) {
  switch (langCode.toLowerCase()) {
    case 'nl': return 'Néerlandais'
    case 'fr': return 'Français'
    case 'en': return 'Anglais'
    default: return langCode
  }
}

watchEffect(() => {
  console.log('[TranslatedComment]', {
    siteLang: siteLang.value,
    originalLang: originalLang.value,
    hasRaw: !!parsedComment.value.raw,
    keys: Object.keys(parsedComment.value),
    shouldShow: shouldShowLanguageLine.value
  })
})
</script>

<style scoped>
p {
  margin-bottom: 0 !important;
}
</style>
